import { Fragment, FunctionComponent, type ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { OnboardingDownloadReport } from '@/pages/OnboardingDownloadReport';
import { OnboardingOverview } from '@/pages/OnboardingOverview';

import { VscSend } from 'react-icons/vsc';
import { MdDelete } from 'react-icons/md';
import { IconType } from 'react-icons/lib';
import { RiLineChartLine } from 'react-icons/ri';
import { IoDocumentTextOutline } from 'react-icons/io5';
import {
  CircleUserRound,
  LucideProps,
  MoreHorizontal,
  Users,
  CopyPlus,
  FileEdit,
} from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigateToPage } from '@client';
import { useIntakeFormActions } from '@client/hooks/useIntakeFormActions';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { cn } from '@client/lib';
import { SessionType, ClientStatus } from '@shared';
import { selectSession } from '@/model/selectors';
import { ModalState, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';

export function ClientActionMenu({
  client,
  sessionAdvisor,
  setModalState,
}: {
  client: Client;
  sessionAdvisor: Advisor;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}) {
  const session = useSelector(selectSession);
  const { navigateToUpdateIntakeForm } = useIntakeFormActions();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const hasFormReference = !!client.formReferenceId;
  const isClientActive = client.clientStatus === ClientStatus.active;
  const showIntakeFormAction = useFormProvider && hasFormReference;
  const buttonText = isClientActive ? 'Update Intake Form' : 'Complete Form';

  function pickClientActions(client: Client) {
    const viewResultsAction = (
      <PageLinkAction
        to={OnboardingOverview}
        client={client}
        sessionAdvisor={sessionAdvisor}
        icon={RiLineChartLine}
      >
        View Results
      </PageLinkAction>
    );

    const skipToDownloadReportAction = (
      <PageLinkAction
        to={OnboardingDownloadReport}
        client={client}
        sessionAdvisor={sessionAdvisor}
        icon={IoDocumentTextOutline}
      >
        Download Report
      </PageLinkAction>
    );

    const addEditPartnerAction = (
      <ButtonAction
        id="link-partner-menuitem"
        onClick={() =>
          setModalState({
            type: 'linkPartner',
            client,
          })
        }
        icon={Users}
      >
        Link/Edit Partner
      </ButtonAction>
    );

    const sendClientSigninLinkAction = (
      <ButtonAction
        onClick={() =>
          setModalState({
            type: 'sendClientSigninLink',
            client,
          })
        }
        icon={VscSend}
      >
        Send Access Link
      </ButtonAction>
    );

    const deleteClientAction = (
      <ButtonAction
        onClick={() =>
          setModalState({
            type: 'delete',
            client,
          })
        }
        icon={MdDelete}
        className="text-red-700"
      >
        Delete Client
      </ButtonAction>
    );

    const viewClientDataAction = (
      <ButtonAction
        onClick={() =>
          setModalState({
            type: 'clientData',
            client,
          })
        }
        icon={CircleUserRound}
      >
        Intake Form Data
      </ButtonAction>
    );

    const updateIntakeFormAction = (
      <ButtonAction
        onClick={async () => {
          await navigateToUpdateIntakeForm(client.clientId);
        }}
        icon={FileEdit}
      >
        {buttonText}
      </ButtonAction>
    );

    const newDuplicateClientAction = (
      <ButtonAction
        onClick={() =>
          setModalState({
            type: 'newDuplicateClient',
            client,
          })
        }
        icon={CopyPlus}
      >
        Duplicate Client
      </ButtonAction>
    );

    const fullActions = [
      viewResultsAction,
      addEditPartnerAction,
      skipToDownloadReportAction,
      viewClientDataAction,
      ...(showIntakeFormAction ? [updateIntakeFormAction] : []),
      sendClientSigninLinkAction,
      ...(session.sessionType === SessionType.wizard ? [newDuplicateClientAction] : []),
      deleteClientAction,
    ];

    const clientTags = client.clientTags;
    if (clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag))) {
      // For error/incomplete clients, show intake form action if applicable, plus delete
      const errorStateActions = [
        ...(showIntakeFormAction ? [updateIntakeFormAction] : []),
        deleteClientAction,
      ];
      return errorStateActions;
    }
    return fullActions;
  }
  const actions = pickClientActions(client);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="border-primary-300 h-8 w-8 border p-0 text-gray-700"
          data-testid="client-action-menu-trigger"
        >
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="client-actions-menu-item-container"
        align="end"
        className="rounded-xl p-2"
      >
        <ActionMenu actions={actions} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActionMenu({ actions }: { actions: ReactNode[] }) {
  return (
    <>
      {actions.map((action, index) => (
        <Fragment key={index}>
          {index === actions.length - 1 && index !== 0 && <DropdownMenuSeparator />}
          {action}
        </Fragment>
      ))}
    </>
  );
}

function PageLinkAction({
  to,
  icon: Icon,
  client,
  sessionAdvisor,
  children,
}: {
  to: FunctionComponent<ClientIdProps>;
  icon: IconType;
  sessionAdvisor: Advisor;
  client: Client;
  children: React.ReactNode;
}) {
  const navigateToPage = useNavigateToPage();

  const menuItem = (
    <DropdownMenuItem
      onClick={() => navigateToPage(to, { clientId: client.clientId })}
      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm"
    >
      <Icon className="h-4 w-4 text-gray-700" />
      {children}
    </DropdownMenuItem>
  );

  // You'll need to pass the sessionAdvisor and client as props to PageLinkAction
  if (sessionAdvisor.advisorId !== client.advisorId) {
    return (
      <AdvisorWarningDialog
        title="Viewing another advisor's client"
        message="Warning: Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
        onContinue={() => navigateToPage(to, { clientId: client.clientId })}
      >
        {/* TODO: This is a bit of a hack because the dropdown menu item was causing the dialog to close when clicked */}
        <Button
          variant="ghost"
          className={cn(
            'relative flex w-full cursor-default select-none items-center justify-start gap-2 rounded-sm px-4 py-1.5 text-sm outline-none transition-colors focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800 dark:focus:text-gray-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
          )}
        >
          <Icon className="mr-1 h-4 w-4 text-gray-500" />
          {children}
        </Button>
      </AdvisorWarningDialog>
    );
  }

  return menuItem;
}

function ButtonAction({
  onClick,
  icon: Icon,
  children,
  className,
  id,
}: {
  onClick: () => void;
  id?: string;
  icon:
    | IconType
    | React.ForwardRefExoticComponent<
        Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
      >;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <DropdownMenuItem
      className={cn(`flex w-full items-center gap-3 px-4 py-2 text-left text-sm`, className)}
      data-testid={id}
      onClick={onClick}
    >
      <Icon className="h-4 w-4 text-gray-500" />
      {children}
    </DropdownMenuItem>
  );
}
